import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    CheckboxGroup,
    Checkbox,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function Contact() {
    const [formData, setFormData] = useState({
        first_name: "",
        surname: "",
        email: "",
        areas_of_interest: [],
        website: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch(
                "https://ozdpcmhyevebjrvqwadt.supabase.co/functions/v1/website-contact",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im96ZHBjbWh5ZXZlYmpydnF3YWR0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODc2NjU3NzEsImV4cCI6MjEwMzI0MTc3MX0.4UneHEviKBMwfabnohr2FI6JWWm00tr89UjmutZo1wo",
                        "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im96ZHBjbWh5ZXZlYmpydnF3YWR0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODc2NjU3NzEsImV4cCI6MjEwMzI0MTc3MX0.4UneHEviKBMwfabnohr2FI6JWWm00tr89UjmutZo1wo",
                    },
                    body: JSON.stringify(formData),
                }
            );

            if (response.ok) {
                setSubmitStatus("success");
                setFormData({
                    first_name: "",
                    surname: "",
                    email: "",
                    areas_of_interest: [],
                    website: "",
                });
            } else {
                setSubmitStatus("error");
            }
        } catch (error) {
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const interestOptions = [
        "Employer after shortlist",
        "Assessment practice",
        "Executive coaching (with battery)",
        "Integrations (Psytech / GeneSys)",
        "White-label portal",
        "Manager-ready reports",
    ];

    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>Talk to us | BetaPersona</title>
                <meta name="description" content="A working session with talent, HRBP or in-house occupational psychology." />
            </Helmet>

            <Container maxW="container.lg" py={{ base: 12, md: 20 }}>
                <VStack spacing={10} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            Talk to us.
                        </Heading>
                        <Text fontSize="xl" color="ink.500" mb={4}>
                            A working session with talent, HRBP or in-house occupational psychology.
                        </Text>
                        <Text fontSize="lg" color="ink.500" fontStyle="italic">
                            This first session is the product, with talent, HRBP or in-house occupational psychology.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            What we cover in a working session
                        </Heading>
                        <VStack align="start" spacing={3}>
                            <Text>
                                • One repository: psychometric, 360 and performance assessments with managed access
                            </Text>
                            <Text>
                                • Live integration with your chosen publishers e.g. Psytech
                            </Text>
                            <Text>
                                • Business-ready packs: derived from publisher reports and mapped to your competency framework
                            </Text>
                            <Text>
                                • White-labelled: client portal carries your brand, not BetaPersona
                            </Text>
                            <Text>
                                • Qualified test use: alignment to BPS / EFPA standards
                            </Text>
                            <Text>
                                • Pricing: depends on volume, instruments, and which components you need
                            </Text>
                            <Text mt={4} fontStyle="italic" fontWeight="600">
                                If that session shows merit, we can set up a free trial. That is the next step we offer; it is not a self-serve signup.
                            </Text>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            Who should attend
                        </Heading>
                        <Text mb={4}>
                            <strong>For employers:</strong> Head of Talent, Head of Assessment, HRBP, in-house occupational psychologist, Head of Recruitment, L&D or talent development.
                        </Text>
                        <Text mb={4}>
                            <strong>For assessment practices:</strong> You already run batteries for clients. One repository across your clients, white-labelled client portals, and packs mapped to their frameworks.
                        </Text>
                        <Text>
                            <strong>For executive coaches who still run a battery:</strong> Store the results, apply access policy, generate the pack. Not for coaching-only practices that run no battery.
                        </Text>
                        <Text mt={6} fontStyle="italic">
                            <strong>Hiring managers:</strong> You receive the pack, but the buyer is talent / HRBP / assessment. Send this to your HRBP or Head of Talent.
                        </Text>
                    </Box>

                    {submitStatus === "success" && (
                        <Alert status="success" borderRadius="md">
                            <AlertIcon />
                            <Box>
                                <AlertTitle>Thank you.</AlertTitle>
                                <AlertDescription>
                                    We will be in touch shortly to arrange a working session.
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {submitStatus === "error" && (
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Box>
                                <AlertTitle>Unable to submit form.</AlertTitle>
                                <AlertDescription>
                                    Please email us at contact@betapersona.com
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    <Box bg="navy.500" color="white" p={8} borderRadius="md">
                        <Heading as="h3" size="lg" mb={6} color="white">
                            Get in touch
                        </Heading>
                        <Box
                            as="form"
                            onSubmit={handleSubmit}
                        >
                            <VStack spacing={6} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel color="white">First name</FormLabel>
                                    <Input
                                        bg="white"
                                        color="ink.500"
                                        value={formData.first_name}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                first_name: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel color="white">Surname</FormLabel>
                                    <Input
                                        bg="white"
                                        color="ink.500"
                                        value={formData.surname}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                surname: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel color="white">Email</FormLabel>
                                    <Input
                                        type="email"
                                        bg="white"
                                        color="ink.500"
                                        value={formData.email}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                email: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel color="white">Areas of interest</FormLabel>
                                    <CheckboxGroup
                                        value={formData.areas_of_interest}
                                        onChange={(values) =>
                                            setFormData({
                                                ...formData,
                                                areas_of_interest: values,
                                            })
                                        }
                                    >
                                        <VStack align="start" spacing={3}>
                                            {interestOptions.map((option) => (
                                                <Checkbox
                                                    key={option}
                                                    value={option}
                                                    colorScheme="orange"
                                                    sx={{
                                                        ".chakra-checkbox__label": {
                                                            color: "white",
                                                        },
                                                    }}
                                                >
                                                    {option}
                                                </Checkbox>
                                            ))}
                                        </VStack>
                                    </CheckboxGroup>
                                </FormControl>

                                <FormControl
                                    style={{ display: "none" }}
                                    aria-hidden="true"
                                >
                                    <FormLabel>Website</FormLabel>
                                    <Input
                                        tabIndex="-1"
                                        autoComplete="off"
                                        value={formData.website}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                website: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl>

                                <Button
                                    type="submit"
                                    bg="accent.orange"
                                    color="white"
                                    size="lg"
                                    isLoading={isSubmitting}
                                    _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                                >
                                    Submit
                                </Button>
                            </VStack>
                        </Box>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Company details
                        </Heading>
                        <VStack align="start" spacing={2}>
                            <Text>
                                <strong>Digitalfields Europe Ltd</strong>
                            </Text>
                            <Text>Company number: 15999199</Text>
                            <Text>85 Great Portland Street</Text>
                            <Text>First Floor</Text>
                            <Text>London W1W 7LT</Text>
                            <Text>United Kingdom</Text>
                        </VStack>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
